import React, { useEffect, useState } from "react";
import { Layout, Menu, Avatar, Dropdown, Space, Button, Tooltip } from "antd";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  HomeOutlined,
  AppstoreOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  GlobalOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";

import { setCookie, getCookie } from "../../utils/cookie";
import locale from "@aligov/global-locale";

import { authService } from "../../services/authService";
import { navigateToLogin } from "../../utils/navigation";

const { Header, Sider, Content } = Layout;

// 语言标映射
const langMap = {
  // "zh-CN": "zh",
  "en-US": "en",
  "th-TH": "th",
};

// 创建带 Tooltip 的菜单项标签
const MenuItemWithTooltip = ({ label, collapsed, isLeaf = true }) => {
  // 检查文本是否可能被截断
  const shouldShowTooltip = label && (label.length > 15 || collapsed);

  if (shouldShowTooltip) {
    return (
      <Tooltip title={label} placement={collapsed ? "right" : "top"}>
        <span className="menu-item-with-tooltip">{label}</span>
      </Tooltip>
    );
  }

  return <span className="menu-item-with-tooltip">{label}</span>;
};

const MainLayout = () => {
  // 菜单默认展开
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const [menuItemsData, setMenuItemsData] = useState([]);

  // 初始化时设置多语言 获取菜单
  useEffect(async () => {
    const { lang } = locale.getLocale();
    if (lang) {
      i18n.changeLanguage(langMap[lang] || lang);
    }

    const menuRes = await authService.getMenu();

    // 如果菜单接口没有返回 跳转到未授权
    if (!menuRes?.data?.length) {
      navigate("/403");
      window.location.reload();

      return;
    }

    const menuData = (menuRes?.data || [])?.sort((a, b) => a?.sort - b?.sort);
    setMenuItemsData(processMenuItems(menuData, collapsed));
  }, []);

  const handleLogout = () => {
    logout();
    navigateToLogin();
  };

  const handleLanguageChange = (lang, key) => {
    i18n.changeLanguage(lang);
    locale.setLang(key);

    // 重新加载刷新菜单
    window.location.reload();
    // setCookie("x-hng", `lang=${key}`);
  };

  // 处理菜单项，为每个标签添加 Tooltip
  const processMenuItems = (items, collapsed) => {
    return items.map((item) => {
      const processedItem = {
        ...item,
        title: item?.label ?? "",
        icon: <AppstoreOutlined />,
      };

      // 判断是否为叶子节点（没有children或children为空数组）
      const isLeaf = !item.children || item.children.length === 0;

      // 为标签添加 Tooltip
      if (item.label || item.title) {
        processedItem.label = (
          <MenuItemWithTooltip
            label={item.label || item.title}
            collapsed={collapsed}
            isLeaf={isLeaf}
          />
        );
      }

      // 递归处理子菜单项
      if (item.children) {
        processedItem.children = processMenuItems(item.children, collapsed);
      }

      return processedItem;
    });
  };

  const originalMenuItems = [
    {
      key: "master-data",
      icon: <AppstoreOutlined />,
      label: t("menu.masterData"),
      children: [
        {
          key: "/warehouse",
          label: t("menu.warehouse"),
        },
        {
          key: "/item",
          icon: <AppstoreOutlined />,
          label: t("menu.item"),
        },
        {
          key: "/category",
          icon: <AppstoreOutlined />,
          label: t("menu.category"),
        },
        {
          key: "/item-store-relationship",
          icon: <AppstoreOutlined />,
          label: t("menu.itemStoreRelationship"),
        },
        {
          key: "/store-item-sale",
          icon: <AppstoreOutlined />,
          label: t("menu.storeItemSale"),
        },
        {
          key: "/store-item-inventory",
          icon: <AppstoreOutlined />,
          label: t("menu.storeItemInventory"),
        },
        {
          key: "/fixture/list",
          icon: <AppstoreOutlined />,
          label: t("menu.fixture"),
        },
        {
          key: "/section-type",
          icon: <AppstoreOutlined />,
          label: t("menu.sectionType"),
        },
        {
          key: "/operator-group",
          icon: <TeamOutlined />,
          label: t("menu.operatorGroup"),
        },
      ],
    },
    {
      key: "space-management",
      icon: <AppstoreOutlined />,
      label: t("menu.editor"),
      children: [
        {
          key: "/share",
          label: t("menu.editor"),
        },
      ],
    },
    {
      key: "alogrithm-configuration",
      icon: <AppstoreOutlined />,
      label: t("menu.algorithmConf"),
      children: [
        {
          key: "/alogrithm-configure",
          label: t("menu.algorithmConf"),
        },
      ],
    },
    {
      key: "planogram",
      icon: <AppstoreOutlined />,
      label: t("menu.planogram"),
      children: [
        {
          key: "/hq-display-groups",
          icon: <AppstoreOutlined />,
          label: t("menu.hqDisplayGroup"),
        },
        {
          key: "/store-group/list",
          icon: <AppstoreOutlined />,
          label: t("menu.storeGroup"),
        },
        {
          key: "/store-display-groups",
          icon: <AppstoreOutlined />,
          label: t("menu.storeDisplayGroup"),
        },
        {
          key: "/store-display-groups/planogram/list",
          icon: <AppstoreOutlined />,
          label: t("menu.planogramList"),
        },
        {
          key: "/templates",
          icon: <AppstoreOutlined />,
          label: t("menu.template"),
        },
        {
          key: "/plans",
          icon: <AppstoreOutlined />,
          label: t("menu.plan"),
        },
        {
          key: "/subtasks",
          icon: <AppstoreOutlined />,
          label: t("menu.subtask"),
        },
        {
          key: "/subtasks4Worker",
          icon: <AppstoreOutlined />,
          label: t("menu.mySubtask"),
        },
      ],
    },
    {
      key: "approval",
      icon: <AppstoreOutlined />,
      label: t("menu.approval"),
      children: [
        {
          key: "/sg-planogram-apply",
          icon: <AppstoreOutlined />,
          label: t("menu.sgApply"),
        },
        {
          key: "/sg-planogram-apply-4-planogram",
          icon: <AppstoreOutlined />,
          label: t("menu.sgApplyPlanogram"),
        },
        {
          key: "/sg-planogram-apply-4-operate",
          icon: <AppstoreOutlined />,
          label: t("menu.sgApplyOperate"),
        },
      ],
    },
    {
      key: "execution",
      icon: <AppstoreOutlined />,
      label: t("menu.storeExecution"),
      children: [
        {
          key: "/execution",
          icon: <AppstoreOutlined />,
          label: t("menu.execution4HQ"),
        },
        {
          key: "/task-list",
          icon: <AppstoreOutlined />,
          label: t("menu.execution"),
        },
        {
          key: "/feedback-list",
          icon: <AppstoreOutlined />,
          label: t("menu.feedbackList"),
        },
        {
          key: "/feedback-list-4-store",
          icon: <AppstoreOutlined />,
          label: t("menu.feedbackList4Store"),
        },
        {
          key: "/container/list",
          icon: <AppstoreOutlined />,
          label: t("menu.container"),
        },
        {
          key: "/shelf",
          icon: <AppstoreOutlined />,
          label: t("menu.shelf"),
        },
        {
          key: "/item-location-relationship",
          icon: <AppstoreOutlined />,
          label: t("menu.itemLocationRelationship"),
        },
        {
          key: "/promotion-location-item",
          icon: <AppstoreOutlined />,
          label: t("menu.promotionLocationItem"),
        },
      ],
    },
    {
      key: "account",
      icon: <TeamOutlined />,
      label: t("account.title"),
      children: [
        {
          key: "/account-management",
          icon: <TeamOutlined />,
          label: t("account.title"),
        },
        {
          key: "/account-management-hq",
          icon: <TeamOutlined />,
          label: t("account.hqTitle"),
        },
        {
          key: "/role",
          icon: <SafetyCertificateOutlined />,
          label: t("role.title"),
        },
      ],
    },
    {
      key: "switch-store",
      icon: <TeamOutlined />,
      label: t("menu.swithStore"),
      children: [
        {
          key: "/switch-warehouse",
          icon: <HomeOutlined />,
          label: t("menu.swithStore"),
        },
      ],
    },
  ];

  // 处理菜单项，添加 Tooltip
  const menuItems = processMenuItems(originalMenuItems, collapsed);

  const userMenuItems = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: t("user.profile"),
      onClick: () => navigate("/profile"),
    },
    // {
    //   key: "settings",
    //   icon: <SettingOutlined />,
    //   label: t("user.settings"),
    //   onClick: () => navigate("/settings"),
    // },
    {
      type: "divider",
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: t("user.logout"),
      onClick: handleLogout,
    },
  ];

  const languageMenuItems = [
    {
      key: "en-US",
      label: "English",
      onClick: () => handleLanguageChange("en", "en-US"),
    },
    {
      key: "th-TH",
      label: "ไทย",
      onClick: () => handleLanguageChange("th", "th-TH"),
    },
    // {
    //   key: "zh-CN",
    //   label: "中文",
    //   onClick: () => handleLanguageChange("zh", "zh-CN"),
    // },
  ];

  return (
    <Layout className="main-layout">
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        theme="dark"
        className="adaptive-sider"
      >
        <div style={{ padding: "16px", textAlign: "center", color: "white" }}>
          <h3 style={{ color: "white", margin: 0 }}>{t("login.title")}</h3>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          defaultOpenKeys={originalMenuItems.map((item) => item.key)} // 默认展开所有一级菜单
          // items={menuItems}
          items={menuItemsData}
          onClick={({ key }) => navigate(key)}
        />
      </Sider>
      <Layout>
        <Header
          style={{
            padding: "0 16px",
            background: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "0 1px 4px rgba(0,21,41,.08)",
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: "16px", width: 64, height: 64 }}
          />

          <Space size="middle">
            <Dropdown
              menu={{ items: languageMenuItems }}
              placement="bottomRight"
            >
              <Button type="text" icon={<GlobalOutlined />}>
                {t("common.language")}
              </Button>
            </Dropdown>

            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <div className="user-avatar">
                <Avatar
                  src={user?.avatar}
                  icon={<UserOutlined />}
                  size="small"
                />
                {!collapsed && (
                  <span className="user-info" style={{ marginLeft: 8 }}>
                    {user?.name}
                  </span>
                )}
              </div>
            </Dropdown>
          </Space>
        </Header>
        <Content className="site-layout-content">
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
